/*
 * The primitive set.
 *
 * Small on purpose. FE-01 will eventually mark some of these `internal` (portal-shell may not
 * import them), and a package that started with forty components would make that split a survey
 * rather than a decision. Everything here is used by the shell or the login screen; nothing is
 * here speculatively.
 *
 * No component takes a `style` prop and none accepts arbitrary class names for layout. That is
 * what keeps FE-05 checkable: if a screen could pass `style={{ marginLeft: 8 }}`, the physical
 * property would live in the app, where the gate scoped to this package would never see it.
 */

import { useEffect, useRef, useState } from 'react';
import { useFloating } from './floating.js';
import type {
  ButtonHTMLAttributes,
  ComponentPropsWithRef,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
} from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  busy?: boolean;
  /**
   * On, for a button that toggles something rather than doing something.
   *
   * A button that opens a panel and closes it again is the same button in two states, and it looked
   * identical in both — so the only way to find out what "Columns" would do was to press it and
   * watch. `aria-expanded` was already being set on those call sites and said so to a screen reader
   * while saying nothing to anybody looking at it, which is the wrong way round for a control whose
   * whole ambiguity is visual.
   *
   * Sets `aria-pressed` as well, unless the caller has already said `aria-expanded` — the two mean
   * different things (*this is on* against *this reveals something below*) and a control that
   * claims both is claiming to be two widgets.
   */
  pressed?: boolean;
  children: ReactNode;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  busy = false,
  pressed,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      className={`tf-button tf-button--${variant} tf-button--${size}${
        pressed ? ' tf-button--pressed' : ''
      }`}
      disabled={disabled ?? busy}
      // Announced rather than merely spun: a screen reader user gets the same "working on it"
      // signal a sighted user gets from the spinner.
      aria-busy={busy || undefined}
      {...(pressed !== undefined && rest['aria-expanded'] === undefined
        ? { 'aria-pressed': pressed }
        : {})}
    >
      {busy ? <Spinner /> : null}
      {children}
    </button>
  );
}

export function Spinner() {
  return <span className="tf-spinner" role="presentation" />;
}

export interface CardProps {
  title?: ReactNode;
  description?: ReactNode;
  /**
   * The card's primary action, rendered beside the title.
   *
   * A slot rather than something callers place themselves, because "Add client" belongs to the
   * list it adds to, and a button floated into position by each screen ends up in a slightly
   * different place on every one of them.
   */
  action?: ReactNode;
  children: ReactNode;
}

export function Card({ title, description, action, children }: CardProps) {
  return (
    <section className="tf-card">
      {title || action ? (
        <div className="tf-card__header">
          <div>
            {title ? <h2 className="tf-card__title">{title}</h2> : null}
            {description ? <p className="tf-card__description">{description}</p> : null}
          </div>
          {action ? <div className="tf-card__action">{action}</div> : null}
        </div>
      ) : (
        description && <p className="tf-card__description">{description}</p>
      )}
      {children}
    </section>
  );
}

/**
 * A glyph, never typed. Drawn so it is the same shape at every size and carries no font-dependent
 * baseline — a `×` character sits differently in every face and is read aloud as "multiplication".
 * The accessible name is always on the button around it.
 */
function CrossGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export interface CloseButtonProps {
  /** FE-03: this package holds no copy. Say what closes, not "Close" — see the note below. */
  readonly label: string;
  readonly onClick: () => void;
}

/**
 * The cross that dismisses the surface it sits in — **top-right, always**.
 *
 * Asked for from a rendered screen, 23 Aug 2026: *"you can add a small cross on the top to just
 * close it. Top right or top left are the positions a user looks for for certain actions, mostly
 * closing / collapsing or expanding / minimising / restoring, depending on Window / Apple user."*
 *
 * That is a real convention, and it is worth being clear about which half of it this takes. **The
 * corner is the habit; the side is the operating system.** A macOS user reaches for the top left
 * and a Windows user for the top right because that is where their window manager puts the
 * controls — but neither expects a panel *inside* an application to follow their window manager,
 * and a product that placed its close where the OS does would put it in two places depending on
 * the visitor. So: top-right, one place, on every machine. What the habit actually buys is the
 * corner, and the corner is what this keeps.
 *
 * **It is never the only way out.** A cross is unlabelled by definition, so every surface that uses
 * one also closes on Escape and keeps a written control beside its primary action — the composer
 * keeps its Cancel. This is the fast way out for somebody who already knows what the panel is; it
 * is not the discoverable one.
 *
 * The label says what it closes rather than the word "Close": a screen-reader user hearing "Close"
 * three times on one screen learns nothing about which of the three they are on.
 */
export function CloseButton({ label, onClick }: CloseButtonProps) {
  return (
    <button type="button" className="tf-close" aria-label={label} onClick={onClick}>
      <CrossGlyph />
    </button>
  );
}

export interface InfoHintProps {
  /**
   * FE-03: the package holds no copy. Two strings, and they are different jobs.
   *
   * `label` names the control for a screen reader — *"What “Live” counts"*, never a bare "Info",
   * because a table with nine of these must not read as nine identical buttons.
   * `children` is the explanation itself.
   */
  readonly label: string;
  readonly children: ReactNode;
}

/**
 * ── The superscript `i` beside a heading nobody can be expected to know ─────────────────────────
 *
 * Asked for from band 5, 23 Aug 2026: *"what is this Live count in the image? Is there a small
 * explanation we can add of each of the header … a small `i` as superscript, and hover over tells
 * you what it is."*
 *
 * The report is a symptom worth naming. Band 5's *Live* is **8** stages summed while the table shows
 * **4** of them as columns, so a row reading `Live 2` above `Submitted 0 · Shortlisted 1 ·
 * Interviewing 0 · Offered 0` looks like arithmetic that does not add up. It adds up perfectly —
 * the missing one is in `sourced` or `screened`, neither of which has a column — but nothing on
 * screen said so, and a reader who catches a table out once stops trusting the rest of it.
 *
 * **So this is not decoration on a self-evident heading.** The rule for using it: a hint belongs
 * where the *name* of a column cannot carry its definition — a total whose parts are not all shown,
 * a word the product has given a narrower meaning than English does, a figure computed rather than
 * counted. A hint on `Owner` would be noise, and noise is what teaches people to stop reading them.
 *
 * ── Hover is the affordance people ask for and never the only one ───────────────────────────────
 *
 * It opens on hover, on keyboard focus, and on click. Hover alone has no equivalent on a touch
 * screen and none at all for a keyboard, and this is the only place the definition exists — so a
 * hover-only version would mean the answer is available to some readers and not others.
 *
 * `aria-describedby` rather than `title`: the native tooltip cannot be styled, cannot be reached by
 * keyboard, and on most platforms waits a second and a half before appearing.
 *
 * Floating for `RowActions`' reason — band 5's table lives in an `overflow: auto` wrapper, and a
 * tooltip that lengthened its container instead of floating over it would produce a scrollbar where
 * an explanation should be.
 */
export function InfoHint({ label, children }: InfoHintProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const hintId = useRef(`tf-info-${(sequence += 1)}`).current;
  const { triggerRef, floatingRef, style } = useFloating<HTMLButtonElement, HTMLSpanElement>(open);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      // Escape closes it wherever focus is, which is the one key a reader will try.
      if (event.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  return (
    <span
      className="tf-info"
      ref={rootRef}
      onPointerEnter={() => setOpen(true)}
      onPointerLeave={() => setOpen(false)}
    >
      <button
        ref={triggerRef}
        type="button"
        className="tf-info__trigger"
        aria-label={label}
        aria-describedby={open ? hintId : undefined}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {/*
          The letter, not an icon font and not an emoji. `aria-hidden` because the button already
          has a name that says what it explains — a screen reader announcing "i" would be reading
          the decoration and dropping the meaning.
        */}
        <span aria-hidden="true">i</span>
      </button>

      {open ? (
        <span
          className="tf-info__bubble"
          id={hintId}
          role="tooltip"
          ref={floatingRef}
          // `manual`, as everywhere else in this file: this owns its own dismissal, and `auto`'s
          // light-dismiss would close the element while React still believed it open.
          popover="manual"
          style={style}
        >
          {children}
        </span>
      ) : null}
    </span>
  );
}

export interface MultiSelectOption {
  readonly value: string;
  readonly label: string;
}

export interface MultiSelectProps {
  /** The generated id from `ToolbarField` / `Field`, so a real `<label>` points at the trigger. */
  readonly id?: string;
  readonly options: readonly MultiSelectOption[];
  /** The chosen values. **Empty means every one**, never none — see below. */
  readonly value: readonly string[];
  readonly onChange: (next: readonly string[]) => void;
  /**
   * FE-03: the package holds no copy, so all five strings come in.
   *
   * `allLabel`  what the trigger reads when nothing is chosen — "All clients".
   * `someLabel` what it reads when several are, given the count — "3 clients".
   * `label`     the accessible name of the list itself.
   * `clearLabel` the option that empties the selection.
   */
  readonly allLabel: string;
  readonly someLabel: (count: number) => string;
  readonly label: string;
  readonly clearLabel: string;
}

/**
 * ── A filter that takes more than one answer ────────────────────────────────────────────────────
 *
 * Asked for from the standup's filter bar, 23 Aug 2026: *"the two filter drop-downs should allow
 * multi select."* The single `<select>` could answer *"how is Meridian this morning"* and could not
 * answer *"how are my three enterprise accounts"*, which is the question a manager actually has.
 *
 * **Empty means everything, and that is not the same as none.** A multi-select whose empty state
 * meant "show nothing" would open on a blank board, so the vocabulary is: no boxes ticked → no
 * narrowing at all. The trigger says so in words rather than leaving a reader to infer it from an
 * empty control, and the same rule is what `matches()` implements on the other side.
 *
 * **Not a native `<select multiple>`.** That control renders as a scrolling list box that is always
 * open, takes vertical space proportional to its options, and — the part that decides it —
 * ctrl-clicks to add and plain-clicks to *replace the whole selection*, which is the single most
 * reliable way to lose a selection somebody spent four clicks building. Checkboxes have no such
 * mode: every click adds or removes exactly one thing.
 *
 * **Floating, for `RowActions`' reason.** The filter bar sits inside regions with `overflow` set,
 * and a menu that lengthens its container instead of floating over it produces a scrollbar where a
 * list should be. `useFloating` puts it in the top layer.
 *
 * The list is a `listbox` with `aria-multiselectable`, not a `menu`: a menu's items are *acts*, and
 * these are *states* that persist after the click. The distinction is what tells a screen reader to
 * announce "selected" at all.
 */
export function MultiSelect({
  id,
  options,
  value,
  onChange,
  allLabel,
  someLabel,
  label,
  clearLabel,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useRef(`tf-multiselect-${(sequence += 1)}`).current;
  const { triggerRef, floatingRef, style } = useFloating<HTMLButtonElement, HTMLDivElement>(open);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    // Back to the trigger, never to the document — see `RowActions`.
    triggerRef.current?.focus();
  };

  const toggle = (option: string) =>
    onChange(
      value.includes(option) ? value.filter((one) => one !== option) : [...value, option],
    );

  /*
   * One choice reads as its own name and several read as a count.
   *
   * Naming two or three of them would make the trigger's width depend on which clients were picked,
   * which is the defect `ToolbarField` exists to prevent — the bar's layout would move as somebody
   * used it.
   */
  const summary =
    value.length === 0
      ? allLabel
      : value.length === 1
        ? (options.find((one) => one.value === value[0])?.label ?? someLabel(1))
        : someLabel(value.length);

  return (
    <div className="tf-multiselect" ref={rootRef}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={`tf-multiselect__trigger${value.length > 0 ? ' tf-multiselect__trigger--set' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className="tf-multiselect__summary">{summary}</span>
        <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false" className="tf-multiselect__chevron">
          <path
            d="M2.5 4.5 6 8l3.5-3.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </button>

      {open ? (
        <div
          className="tf-multiselect__list"
          id={listId}
          role="listbox"
          aria-multiselectable="true"
          aria-label={label}
          ref={floatingRef}
          // `manual`, for `RowActions`' reason: this handles its own dismissal, and `auto`'s
          // light-dismiss would close the element while React still believed it open.
          popover="manual"
          style={style}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              close();
            }
          }}
        >
          {/* Only when there is something to clear. A permanently present "Clear" on an untouched
              filter is a control that does nothing, at the top of the list, every time. */}
          {value.length > 0 ? (
            <button
              type="button"
              className="tf-multiselect__clear"
              onClick={() => {
                onChange([]);
                close();
              }}
            >
              {clearLabel}
            </button>
          ) : null}

          {options.map((option) => {
            const checked = value.includes(option.value);
            return (
              <div
                key={option.value}
                role="option"
                aria-selected={checked}
                className="tf-multiselect__option"
              >
                <label>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export interface GroupToggleProps {
  /** FE-03: the package holds no copy. Name the group — "Collapse Needs a look", never "Collapse". */
  readonly label: string;
  readonly expanded: boolean;
  readonly onToggle: () => void;
}

/**
 * The chevron that folds one group of a list away — asked for from the rendered standup, 23 Aug
 * 2026: *"a small icon to help expand then collapse"*, on the header that already shows the count.
 *
 * **Why this is not `Disclosure`.** `Disclosure` owns its whole head: it draws the summary line,
 * puts the chevron at the start of it and takes an `aside` on the end. The bars this replaces are
 * already composed — a caps label, a sentence of prose, a count in the display face at the far end
 * — and were built that way on purpose, because they are the section rule that separates one group
 * of rows from the next. Wrapping them in a second head would put two headers on one group.
 *
 * So this is the control alone, for a header that already exists. The pair is deliberate and
 * neither one is a worse version of the other.
 *
 * **It never carries the count.** The count belongs to the header, visible in both states, because
 * a collapsed group that will not say how much is behind it has stopped being a disclosure and
 * become a filter.
 */
export function GroupToggle({ label, expanded, onToggle }: GroupToggleProps) {
  return (
    <button
      type="button"
      className="tf-change__toggle"
      aria-expanded={expanded}
      aria-label={label}
      onClick={onToggle}
    >
      <svg
        viewBox="0 0 12 12"
        aria-hidden="true"
        focusable="false"
        className="tf-change__chevron"
      >
        <path
          d="M2.5 4.5 6 8l3.5-3.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    </button>
  );
}

export interface BannerProps {
  /**
   * `warning` is not a weaker `danger`, and the parse-review screen is what made the difference
   * matter. Danger says something went wrong; warning says something needs a human to look at it,
   * which is exactly the state a low-confidence extraction is in. Rendering the second as the
   * first teaches recruiters that red means "carry on", which is the property red must not lose.
   */
  tone?: 'danger' | 'warning' | 'info';
  children: ReactNode;
  /**
   * Makes the banner closable, with the label for the control — added 18 Aug 2026.
   *
   * ── Why `Banner` and deliberately not `InlineAlert` ─────────────────────────────────────────
   *
   * This component's own header draws the line and it is the line that decides this: a `Banner` is
   * about **something that just happened**, and an `InlineAlert` is about **a state the panel is in
   * and stays in until somebody acts**. You can dismiss news. Dismissing a standing condition would
   * hide the thing that is still true — a stale artifact, an inherited plan — and leave a screen
   * quietly lying about itself, so `InlineAlert` has no equivalent and should not get one.
   *
   * ── Why the label is a prop ─────────────────────────────────────────────────────────────────
   *
   * FE-03: this package holds no copy. A bare × is not an accessible name, so the caller supplies
   * one out of the catalogue, and passing it is what opts a banner in. Uncontrolled on purpose —
   * "I have read this" is a fact about one reader in one session and never worth lifting into a
   * screen's state.
   */
  dismiss?: { readonly label: string };
}

export function Banner({ tone = 'info', children, dismiss }: BannerProps) {
  const [closed, setClosed] = useState(false);
  if (closed) return null;

  return (
    // `role="alert"` only on the danger tone: an assertive live region for informational copy
    // interrupts a screen reader for no reason, and a warning a person is about to read anyway
    // does not need to interrupt either.
    <div className={`tf-banner tf-banner--${tone}`} role={tone === 'danger' ? 'alert' : undefined}>
      <div className="tf-banner__body">{children}</div>
      {dismiss ? (
        <button
          type="button"
          className="tf-banner__dismiss"
          aria-label={dismiss.label}
          onClick={() => setClosed(true)}
        >
          <CrossGlyph />
        </button>
      ) : null}
    </div>
  );
}

export interface StackProps {
  direction?: 'row' | 'column';
  gap?: 1 | 2 | 3 | 4 | 5 | 6;
  align?: 'start' | 'center' | 'end' | 'baseline' | 'stretch';
  justify?: 'start' | 'center' | 'end' | 'space-between';
  wrap?: boolean;
  /**
   * Give every child the same width. Rows only.
   *
   * ── Why this is opt-in rather than how rows always behave ──────────────────────────────────────
   *
   * A flex child defaults to `flex: 0 1 auto`, so its base size is its own max-content width. Two
   * form fields side by side therefore render at whatever width their *contents* happen to need,
   * and a field with a hint under it, or a longer option in its select, comes out visibly wider
   * than its neighbour. Worse, it changes width as the content changes: the job form's engagement
   * select grows the moment a client is chosen, because that is when its hint appears, so the row
   * visibly rearranges itself while somebody is filling it in.
   *
   * That is nearly always wrong for a row of controls and nearly always right for a row of chips,
   * buttons or capsules, which should hug what they contain. `direction="row"` is used in dozens of
   * places across the apps and most of them are the second kind — so this is a prop rather than a
   * change to the default, and the call sites that mean "these are columns of a form" say so.
   */
  equal?: boolean;
  children: ReactNode;
}

/**
 * Flow layout. `row` and `column` are writing-mode relative in flexbox already, which is why this
 * needs no RTL handling of its own — the physical direction follows the document.
 */
export function Stack({
  direction = 'column',
  gap = 4,
  align = 'stretch',
  justify = 'start',
  wrap = false,
  equal = false,
  children,
}: StackProps) {
  return (
    <div
      // A class rather than an inline style, because the rule this needs applies to the *children*
      // and inline styles cannot reach them.
      className={equal ? 'tf-stack--equal' : undefined}
      style={{
        display: 'flex',
        flexDirection: direction,
        gap: `var(--space-${gap})`,
        alignItems: align,
        justifyContent: justify,
        flexWrap: wrap ? 'wrap' : 'nowrap',
        minInlineSize: 0,
      }}
    >
      {children}
    </div>
  );
}

export interface FactsProps {
  /** Rendered in order. A record would not survive JSON round-tripping with order intact. */
  items: readonly { readonly term: ReactNode; readonly value: ReactNode }[];
}

/** A description list, for "what am I looking at" panels. */
export function Facts({ items }: FactsProps) {
  return (
    <dl className="tf-facts">
      {items.map((item, index) => (
        <div key={index} style={{ display: 'contents' }}>
          <dt>{item.term}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Code({ children }: { children: ReactNode }) {
  return <code className="tf-code">{children}</code>;
}

export interface TagProps {
  /**
   * Tones exist because a tag is used for state as well as for labels.
   *
   * Colour alone never carries the meaning — every tone renders text a person can read, so the
   * distinction survives a monochrome screen and a colour-blind reader (§26). What the tone adds
   * is the ability to *scan*: a queue of two hundred rows is read by shape and colour first, and
   * "which of these need me" has to be answerable without reading each one.
   */
  tone?: 'neutral' | 'success' | 'warning' | 'danger';
  /**
   * Makes the tag removable — the chip that stands for one item in an editable set.
   *
   * The button is inside the tag rather than beside it because the two are one target in the
   * reader's mind and one tab stop in the tree. `removeLabel` is required when this is given, and
   * it names *what* is being removed ("Remove Java"), not the gesture: a screen reader announcing
   * "Remove, Remove, Remove" down a row of twenty chips has told the listener nothing.
   */
  onRemove?: () => void;
  removeLabel?: string;
  children: ReactNode;
}

export function Tag({ tone = 'neutral', onRemove, removeLabel, children }: TagProps) {
  return (
    <span className={`tf-tag tf-tag--${tone}`}>
      {children}
      {onRemove ? (
        <button
          type="button"
          className="tf-tag__remove"
          aria-label={removeLabel}
          onClick={onRemove}
        >
          {/* A multiplication sign, not the letter x — it is the right glyph and it renders the
              same in every font the product ships. `aria-hidden` because the label above is what
              a screen reader should read. */}
          <span aria-hidden="true">×</span>
        </button>
      ) : null}
    </span>
  );
}

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /**
   * Marks the label as required — a visible asterisk plus an `aria-label` for anyone not seeing it.
   *
   * Added 15 Sep 2026 for Job Title, which was already enforced both client- and server-side but
   * gave no visual sign of it until the browser's own validation fired on submit. Opt-in rather than
   * inferred from the control's own `required` attribute: `Field` never sees that attribute, since
   * it takes a render function rather than the control itself, and this is deliberately not being
   * retrofitted onto every required field on every screen in this pass — that's real scope, just not
   * this scope.
   */
  required?: boolean;
  /** The control. Given the generated id so the label points at it. */
  children: (props: { id: string; 'aria-invalid': boolean }) => ReactNode;
}

let fieldSequence = 0;

/**
 * A labelled control.
 *
 * Takes a render function rather than wrapping an input, so the caller keeps its own element and
 * its own props while the label, hint and error stay consistent. The alternative — a component
 * per input type — multiplies with every control the product needs.
 */
export function Field({ label, hint, error, required, children }: FieldProps) {
  // A module-scoped counter rather than useId(): this package has no React version floor beyond
  // 19, and the id only has to be unique within a document.
  const id = `tf-field-${(fieldSequence += 1)}`;

  return (
    <div className="tf-field">
      <label className="tf-field__label" htmlFor={id}>
        {label}
        {required ? (
          <span className="tf-field__required" aria-label="required">
            {' '}
            *
          </span>
        ) : null}
      </label>
      {children({ id, 'aria-invalid': Boolean(error) })}
      {hint && !error ? <span className="tf-field__hint">{hint}</span> : null}
      {error ? (
        <span className="tf-field__error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export const FIELD_ROW_WIDTHS = ['sm', 'md', 'lg'] as const;
export type FieldRowWidth = (typeof FIELD_ROW_WIDTHS)[number];

export interface FieldRowProps {
  /**
   * The column *measure*, not a column count.
   *
   * `sm` for numbers and short enums, `md` for the ordinary control, `lg` where the value itself is
   * long — a select whose options carry a client name, say. The number of columns falls out of the
   * measure and the space available, which is what makes the same markup work in a 46rem flyout and
   * on a phone without a breakpoint being written anywhere.
   */
  width?: FieldRowWidth;
  children: ReactNode;
}

/**
 * Fields side by side, on a grid whose column *width* is fixed and whose column *count* is not.
 *
 * ── Why this exists when `Stack equal` already does ─────────────────────────────────────────────
 *
 * `Stack direction="row" equal` gives every child `flex: 1 1 0`, which is right on a page whose
 * width is known: the job form uses it and two fields come out as two halves. It has two properties
 * that are wrong in a panel, and the interview composer is where both bit.
 *
 * **It cannot wrap.** A basis of zero means the children shrink for ever rather than moving to the
 * next line, so three `datetime-local` controls in one row stay three across at 320px and each ends
 * up too narrow to show a date. `flex-wrap` does not help, because nothing ever overflows.
 *
 * **It divides whatever it is given.** Two fields in a 44rem panel become two 22rem controls — and
 * a five-option select rendered 22rem wide is the same complaint that started this pass, at half
 * scale. A form does not want its controls to be as wide as the space; it wants them to be as wide
 * as their contents deserve, and the space left over to stay empty.
 *
 * So: `repeat(auto-fill, minmax(<measure>, 1fr))`. **`auto-fill` rather than `auto-fit`, and that
 * is the whole difference** — `auto-fit` collapses the tracks nothing lands in and lets the rest
 * absorb the width, which reintroduces exactly the stretching above the moment a row holds one
 * field. `auto-fill` keeps the empty track, so a lone control is one column wide, two controls line
 * up under two controls in the row above, and the grid reads as a grid down the whole form.
 */
export function FieldRow({ width = 'md', children }: FieldRowProps) {
  return <div className={`tf-field-row tf-field-row--${width}`}>{children}</div>;
}

/**
 * One field, two columns wide — for the control whose *value* is long rather than its label.
 *
 * A wrapper rather than a prop on `Field`, because the span is a fact about the row and not about
 * the field: the same job picker is one column in a form that is mostly numbers and two in a form
 * that is mostly text. It clamps rather than overflowing when the row has only one column, which is
 * what makes it safe to use without asking how wide the screen is.
 */
export function FieldSpan({ children }: { children: ReactNode }) {
  return <div className="tf-field-span">{children}</div>;
}

export interface FormSectionProps {
  title: ReactNode;
  /** One line under the heading — what this group of fields is for, never how to fill it in. */
  hint?: ReactNode;
  children: ReactNode;
}

/**
 * A named group of fields, with a real heading over it.
 *
 * ── The defect this closes ──────────────────────────────────────────────────────────────────────
 *
 * Nineteen places across the recruiter app opened a group of controls with
 * `<span className="tf-field__label">`. That is a *field's* label class on something that labels no
 * field, which has three costs and only the first is visible: it reads at the same weight as the
 * labels beneath it, so the group does not look like a group; a screen reader gets a run of text
 * rather than a landmark, so the panel has one heading and no structure under it; and nothing keeps
 * the spacing above and below it from being the same, which is what actually makes a heading look
 * unattached to what it heads.
 *
 * The last one is why this is a component rather than an instruction to use `SectionTitle`.
 * `SectionTitle` carries `margin-block-end`, which is correct where it sits directly above content
 * and wrong inside a grid that already has a gap — it lands *further* from what it labels than from
 * the group before it. Here the heading and its fields are one grid with one gap, and the space
 * between sections is the parent's.
 */
export function FormSection({ title, hint, children }: FormSectionProps) {
  return (
    <section className="tf-form-section">
      <h3 className="tf-form-section__title">{title}</h3>
      {hint ? <p className="tf-form-section__hint">{hint}</p> : null}
      <div className="tf-form-section__body">{children}</div>
    </section>
  );
}

export interface CheckOptionProps {
  name: ReactNode;
  hint?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

/** One toggleable capability. Used for module entitlements. */
export function CheckOption({ name, hint, checked, onChange }: CheckOptionProps) {
  return (
    <label className="tf-check">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
      <span className="tf-check__label">
        <span className="tf-check__name">{name}</span>
        {hint ? <span className="tf-check__hint">{hint}</span> : null}
      </span>
    </label>
  );
}

export interface ToggleProps {
  /** The thing being switched on or off, not the state — "Full time", not "Enabled". */
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Optional word for the current state, shown beside the switch. */
  stateLabel?: ReactNode;
}

/**
 * A switch, for a setting that takes effect the moment it moves.
 *
 * Distinct from `CheckOption`, and the distinction is about *when the change lands* rather than
 * about looks. A checkbox states an intention that some later Save commits; a switch is the
 * commit. Everything on this screen writes immediately, so a checkbox was making a promise the
 * screen does not keep — the value was already saved by the time the user looked for a button.
 *
 * Kept as a real `input type="checkbox"`, restyled. A `div` with `role="switch"` would have to
 * re-implement focus, the space key, form association and the accessibility tree; a checkbox with
 * `role="switch"` is the same control with a more accurate name, and every one of those behaviours
 * is already correct.
 *
 * **The input is the painted control**, via `appearance: none` and a pseudo-element thumb — not a
 * transparent input laid over decorative spans. The first version did exactly that, and Playwright
 * would not drive it: the element resolved, then failed its actionability check and the test timed
 * out on a control a person could see perfectly well. An input that *is* the switch has nothing
 * over it to intercept the click, which is the same reason it works for a mouse and for a test.
 */
export function Toggle({ label, checked, onChange, disabled = false, stateLabel }: ToggleProps) {
  return (
    <label className="tf-toggle">
      <input
        type="checkbox"
        role="switch"
        className="tf-toggle__input"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
      <span className="tf-toggle__label">{label}</span>
      {stateLabel ? <span className="tf-toggle__state">{stateLabel}</span> : null}
    </label>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return <h3 className="tf-section-title">{children}</h3>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="tf-empty">{children}</p>;
}

/* ── The signature element ──────────────────────────────────────────────────────────────────── */

export interface KpiCardProps {
  /** Small-caps, above the number. See the note on ordering below. */
  label: ReactNode;
  /** The number. Rendered at display size with tabular figures; pre-format it for the locale. */
  value: ReactNode;
  /** A qualifier inline beside the number — "of 24", "+3 this week". Never its own row. */
  detail?: ReactNode;
  /**
   * A second line **under** the number, where `detail` is beside it.
   *
   * The two are not interchangeable and the split is the point. `detail` qualifies the figure and
   * has to be read with it — *62 **across 6 jobs*** is one fact. A footnote is a different fact
   * about the same subject — *oldest 19 days · 3 stuck in all* — and putting that inline either
   * pushes the number off the card or makes it look like part of it.
   *
   * Keep it to one short clause. It is the smallest type on the card, and a footnote that wraps to
   * three lines is a card that has become a paragraph.
   */
  footnote?: ReactNode;
  /**
   * Marks this as the card the screen wants read first. **At most one per strip.**
   *
   * Change Order 2 §1.2 calls the KPI card "the one place to spend boldness", and the failure mode
   * of that instruction is four cards shouting at each other. The accent rule is the boldness, and
   * it only means "this one" if the others do not have it.
   */
  emphasis?: boolean;
  /**
   * The rail on the inline-start edge, and what it says.
   *
   * ── `info` and `neutral` added 30 Aug 2026, and why every card now takes one ─────────────────
   *
   * *"On the clients home page add the colours to the cards. Similarly on Jobs, only one has
   * colour. Candidates home page also — not all cards have colour on the left."*
   *
   * The four tones that existed covered only the cards that are somebody's turn or somebody's
   * problem, so a strip of four had one rail and three blank edges — which reads as three
   * unfinished cards rather than as one urgent one. `info` is the missing case: **a figure that is
   * context**, neither good news nor a task, which is what a database total or a month's arrivals
   * actually is.
   *
   * The grammar the three landings now share, in the same order on each:
   *
   *   `info`       — context. How big is the thing.
   *   `attention`  — your turn. Something is waiting on this agency.
   *   `critical`   — a problem. Something has gone wrong and nobody has noticed.
   *   `positive`   — the same card when its figure is zero, because *nothing waiting* is a result
   *                  worth stating rather than an absence worth greying out.
   *
   * That last row is the part that keeps this honest rather than decorative. A card does not get a
   * colour for having one; it gets the colour its own figure earns, and a cleared queue earns
   * green.
   *
   * `neutral` is the slate rail for a figure that is genuinely inert — declared because the scale
   * has one and a caller should not have to reach for `info` to mean "no reading at all".
   */
  tone?: 'default' | 'info' | 'neutral' | 'positive' | 'attention' | 'critical';
  /**
   * Makes the card a control. Every KPI on the Jobs landing applies a filter (§3), so most of them
   * are — and a card that is *not* must look different from one that is, or the first unclickable
   * card teaches everybody to stop trying.
   *
   * **Mutually exclusive with `breakdown`.** An interactive card renders as a `<button>`, and a
   * button containing buttons is invalid HTML that behaves differently in every browser. A card
   * with a ladder puts the controls on the rungs; the headline figure above them is a summary.
   */
  onClick?: () => void;
  /**
   * Pressed, because this card's filter is the one currently applied — added 30 Aug 2026 when the
   * Jobs landing moved from `KpiBand` to this component and brought the behaviour with it.
   *
   * A card that is narrowing the list beneath it and does not say so is how a short list gets read
   * as a quiet week. `KpiCell` has carried this since it was written; the difference was only that
   * no strip had needed it yet.
   */
  active?: boolean;
  /**
   * A short ladder under the figure — added 30 Aug 2026 for the Jobs landing's *No action* card.
   *
   * The case it exists for: a single figure that is only useful **decomposed**. *No action: 24* is
   * a number; *a month or more 24, two months or more 9, three months or more 3* is an order of
   * work, because the ones at three months are a different conversation from the ones at five
   * weeks and a recruiter clearing a desk wants the far end first.
   *
   * Cumulative rungs by nature — each row counts everything at or beyond it — which is why the top
   * rung repeats the headline rather than restating a slice of it. That repetition is the price of
   * every rung being a filter, and it is worth paying: a ladder where one row is a control and the
   * one above it is decoration is a ladder people click the wrong part of.
   *
   * **Keep it to three or four rungs.** Past that the card stops being a figure with a shape and
   * becomes a table that happens to be in a strip, and `DataTable` is better at being a table.
   */
  breakdown?: KpiRung[];
}

/** One rung of a {@link KpiCardProps.breakdown} ladder. Clickable when it filters something. */
export interface KpiRung {
  key: string;
  label: ReactNode;
  value: ReactNode;
  /** Pressed, because this rung's filter is the one currently applied. */
  active?: boolean;
  onClick?: () => void;
}

/**
 * The KPI card — Home and the Jobs landing both lead with a strip of these, so it is the first
 * thing anybody sees and the thing they see most often.
 *
 * **Label above the number, not below.** The eye lands on the number; it reads up for what the
 * number means only if it does not already know. A label underneath is read *after* the reader has
 * already decided the card is irrelevant.
 *
 * **The number is sans, not the display face**, and that is a deliberate split from §1.2's wording.
 * A serif's numerals are old-style — they hang below the baseline and vary in height, which is
 * lovely in a paragraph and wrong in a column of figures somebody scans. Design language §3.1.
 */
export function KpiCard({
  label,
  value,
  detail,
  footnote,
  emphasis = false,
  tone = 'default',
  onClick,
  active = false,
  breakdown,
}: KpiCardProps) {
  // The rungs are the controls on a card that has them, so the card itself must not also be one —
  // see `onClick`. Dropped rather than thrown on, because a strip that renders is more useful than
  // a screen that does not, and lint will not let this reach a build unnoticed.
  const interactive = onClick !== undefined && breakdown === undefined;

  const content = (
    <>
      <span className="tf-kpi__label">{label}</span>
      <span className="tf-kpi__row">
        <span className="tf-kpi__value">{value}</span>
        {detail ? <span className="tf-kpi__detail">{detail}</span> : null}
      </span>
      {footnote ? <span className="tf-kpi__footnote">{footnote}</span> : null}
      {breakdown && breakdown.length > 0 ? (
        <ul className="tf-kpi__ladder">
          {breakdown.map((rung) => {
            const inner = (
              <>
                <span className="tf-kpi__rung-label">{rung.label}</span>
                <span className="tf-kpi__rung-value">{rung.value}</span>
              </>
            );
            return (
              <li key={rung.key}>
                {rung.onClick ? (
                  <button
                    type="button"
                    className={`tf-kpi__rung${rung.active ? ' tf-kpi__rung--active' : ''}`}
                    aria-pressed={rung.active ?? false}
                    onClick={rung.onClick}
                  >
                    {inner}
                  </button>
                ) : (
                  <span className="tf-kpi__rung">{inner}</span>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </>
  );

  const className = [
    'tf-kpi',
    emphasis ? 'tf-kpi--emphasis' : '',
    tone === 'default' ? '' : `tf-kpi--${tone}`,
    interactive ? 'tf-kpi--interactive' : '',
    active ? 'tf-kpi--active' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // A real `button` when it acts like one. A clickable `div` with a role bolted on is a keyboard
  // trap waiting to be discovered by somebody who cannot use a mouse.
  return interactive && onClick ? (
    <button type="button" className={className} aria-pressed={active} onClick={onClick}>
      {content}
    </button>
  ) : (
    <div className={className}>{content}</div>
  );
}

/** The strip the cards sit in. A grid rather than a flex row, so all four share one height. */
export interface ReadCardProps {
  /** The question this card answers, in two or three words. Uppercase is the stylesheet's job. */
  label: ReactNode;
  /** The answer, as a short phrase. Not a figure to be trended — see the stylesheet's note. */
  value: ReactNode;
  /** A qualifier read *with* the value, on the same line. "→ ₹18–20L". */
  detail?: ReactNode;
  /** The sentence underneath that says how the answer was reached. */
  note?: ReactNode;
  /**
   * `good` and `warn` colour the value; `flag` tints the whole card.
   *
   * Three, not five. A strip where every card is coloured is a traffic light nobody reads, so the
   * neutral default is the common case and `flag` is meant to be rare — it exists for the one card
   * whose entire job is to stop somebody before they act.
   */
  tone?: 'default' | 'good' | 'warn' | 'flag';
  /** When the card goes somewhere. A card with no destination renders as a plain element. */
  onClick?: () => void;
}

/**
 * One short answer, with how it was reached underneath — the candidate record's read strip.
 *
 * Distinct from `KpiCard`, and the distinction is not cosmetic: a KPI is **a number compared over
 * time**, and this is **a sentence that answers a question and points at its evidence**.
 * "Available 15 Sep · 60-day notice, serving" cannot be trended, and rendering it at
 * `--text-display` — which is what happened when this reused `KpiCard` — produces two columns of
 * enormous type where five compact cards belong.
 */
export function ReadCard({ label, value, detail, note, tone = 'default', onClick }: ReadCardProps) {
  const className = ['tf-read', tone === 'default' ? '' : `tf-read--${tone}`]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      <span className="tf-read__label">{label}</span>
      <span className="tf-read__value">
        {value}
        {detail ? <span className="tf-read__detail">{detail}</span> : null}
      </span>
      {note ? <span className="tf-read__note">{note}</span> : null}
    </>
  );

  // A real `button` when it acts like one. A clickable `div` with a role bolted on is a keyboard
  // trap waiting to be found by somebody who cannot use a mouse.
  return onClick ? (
    <button type="button" className={className} onClick={onClick}>
      {content}
    </button>
  ) : (
    <div className={className}>{content}</div>
  );
}

export function ReadStrip({ children }: { children: ReactNode }) {
  return <div className="tf-read-strip">{children}</div>;
}

export function KpiStrip({
  children,
  compact = false,
}: {
  children: ReactNode;
  /**
   * Tighter padding and a smaller figure — for a strip whose height is set by one tall card.
   *
   * The Jobs landing is the case: one of its four carries a three-rung ladder, the strip is a grid
   * so all four share that height, and the other three then hold a figure and a lot of air. The
   * shrink lands on the padding and the ladder's leading rather than on the figure, which is the
   * one thing on a card somebody reads from across a desk.
   */
  compact?: boolean;
}) {
  return <div className={`tf-kpi-strip${compact ? ' tf-kpi-strip--compact' : ''}`}>{children}</div>;
}

/* ── Status, people, and the states nobody designs ──────────────────────────────────────────── */

export type StatusTone = 'neutral' | 'positive' | 'attention' | 'critical' | 'info';

export interface StatusChipProps {
  /**
   * Required, and it is text rather than a node for one reason: colour never carries the meaning
   * alone (spec §26). A chip that could be given an icon would eventually be given only an icon.
   */
  label: string;
  tone?: StatusTone;
}

/**
 * What a row *is* — a stage, a priority, a margin health.
 *
 * Distinct from `Tag`, which labels a thing (a skill, an entitlement). The separation matters
 * because the palettes are different on purpose: `StatusChip` draws from the muted semantic scale,
 * so a board of two hundred rows does not scream, while `Banner`'s `danger` stays loud and rare.
 * Design language §2.2.
 */
export function StatusChip({ label, tone = 'neutral' }: StatusChipProps) {
  return <span className={`tf-chip tf-chip--${tone}`}>{label}</span>;
}

/** One line of the hover card: what it is, and what it says. */
export interface PersonFact {
  label: string;
  value: string;
}

export interface PersonCapsuleProps {
  name: string;
  /** Shown under the name — a role, a team, an email. Optional; the capsule works without it. */
  detail?: string;
  /**
   * The word that marks this person as the viewer, rendered in brackets **beside** the name.
   *
   * Separate from `detail` rather than another use of it, and the reason is layout before it is
   * meaning. `detail` stacks under the name, which is right for a role or a sign-in time — they
   * are facts *about* the person and they are long. It is wrong for this one: an owner column with
   * "You" under one row's name and nothing under the others makes that row taller than its
   * neighbours, so a list of twelve owners has one row out of step for a word.
   *
   * The meaning differs too, which is why the fix is a second prop rather than a flag on the first.
   * A role describes the person; this describes their relationship to whoever is reading. The
   * brackets are the component's, not the caller's — every call site would otherwise decide the
   * punctuation separately and one of them would eventually disagree.
   */
  you?: string;
  size?: 'sm' | 'md';
  /**
   * Extra facts revealed on hover or keyboard focus — change order 3 §6's "usual hover detail".
   *
   * Optional, and the capsule is unchanged without it: a name in a dense table should not become
   * an interactive target just because the component *can* carry more.
   */
  facts?: readonly PersonFact[];
}

/**
 * A person, as initials and a name.
 *
 * Initials rather than a photo because `membership` carries no avatar yet (Change Order 2 §7 adds
 * the column). Deriving them here rather than at every call site is what keeps "Priya Ramanathan"
 * and "priya ramanathan" rendering the same two letters.
 *
 * ── The hover card ──────────────────────────────────────────────────────────────────────────────
 *
 * **Hover is the trigger it is named after, and it is not the only one.** The card opens on
 * `:hover` *and* `:focus-within`, and the capsule becomes focusable only when there are facts to
 * show. Hover alone would put the detail out of reach of a keyboard entirely and out of reach of a
 * touch screen, which has no hover state to give — so what looks like a nicety would be a fact
 * available to some people and not others.
 *
 * **It was CSS, and it is now state — revised 12 Aug 2026.** The original argued that a JavaScript
 * implementation buys placement flipping near a viewport edge and costs a re-render per pointer
 * move. The first half turned out to be the requirement rather than a luxury: this card lives in
 * the job record's sticky header, inside a `.tf-panel`, and `overflow: hidden` cut it off mid-word.
 * Nothing painted inside a clipped box can escape it, so the card had to leave the box, and a
 * fixed-position element must be placed by somebody.
 *
 * The cost turned out not to be real. The renders are on pointer *enter* and *leave*, not on move —
 * two per capsule the pointer crosses, which is nothing next to the table re-rendering around it.
 * And the case the old comment worried about, a card left open when the row unmounts, is now the
 * one that cannot happen: the state unmounts with the row.
 *
 * The card is `aria-hidden` because every fact in it is also in the DOM for a screen reader through
 * the description below — it is a visual affordance, and announcing it twice is worse than not
 * announcing it at all.
 */
export function PersonCapsule({ name, detail, you, size = 'md', facts }: PersonCapsuleProps) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  const hasCard = Boolean(facts && facts.length > 0);
  const [open, setOpen] = useState(false);
  const { triggerRef, floatingRef, style } = useFloating<HTMLSpanElement, HTMLSpanElement>(
    hasCard && open,
  );

  return (
    <span
      ref={triggerRef}
      className={`tf-person tf-person--${size}${hasCard ? ' tf-person--has-card' : ''}`}
      // Focusable only when there is something to reveal. A tab stop that opens nothing is a tab
      // stop somebody has to press past on every row of the table.
      tabIndex={hasCard ? 0 : undefined}
      // Pointer rather than mouse events, so a touch screen — which has no hover at all — opens the
      // card on tap. `onFocus`/`onBlur` bubble in React, which is what keeps the keyboard path
      // working now that `:focus-within` no longer drives it.
      {...(hasCard
        ? {
            onPointerEnter: () => setOpen(true),
            onPointerLeave: () => setOpen(false),
            onFocus: () => setOpen(true),
            onBlur: () => setOpen(false),
            // Escape closes it. A card that can only be dismissed by moving the pointer is one a
            // keyboard user cannot get out from under.
            onKeyDown: (event: ReactKeyboardEvent) => {
              if (event.key === 'Escape') setOpen(false);
            },
          }
        : {})}
    >
      {/* Hidden from the accessibility tree: the name is right beside it, and "PR, Priya
          Ramanathan" is the same information twice. */}
      <span className="tf-person__mark" aria-hidden="true">
        {initials}
      </span>
      <span className="tf-person__body">
        {/* The name and the viewer marker share a line; anything in `detail` still sits under
            both. Two elements rather than one string so the marker can be sized and coloured as
            the secondary thing it is without the name inheriting either. */}
        <span className="tf-person__line">
          <span className="tf-person__name">{name}</span>
          {you ? <span className="tf-person__you">({you})</span> : null}
        </span>
        {detail ? <span className="tf-person__detail">{detail}</span> : null}
      </span>
      {hasCard && open ? (
        <span
          className="tf-person__card"
          role="presentation"
          aria-hidden="true"
          ref={floatingRef}
          popover="manual"
          style={style}
        >
          {facts?.map((fact) => (
            <span className="tf-person__fact" key={fact.label}>
              <span className="tf-person__fact-label">{fact.label}</span>
              <span className="tf-person__fact-value">{fact.value}</span>
            </span>
          ))}
        </span>
      ) : null}
      {/* The same facts, once, for anybody not looking at the card. Visually hidden rather than
          omitted: the alternative is a name that reads as a name to a screen reader while sighted
          users can see it is also a role and an email. */}
      {hasCard ? (
        <span className="tf-visually-hidden">
          {facts?.map((fact) => `${fact.label}: ${fact.value}`).join('. ')}
        </span>
      ) : null}
    </span>
  );
}

export interface EmptyStateProps {
  title: ReactNode;
  /** One sentence. If it needs two, the screen is explaining something the design should say. */
  description?: ReactNode;
  /** One primary action. Two actions is a decision the reader has not been given enough to make. */
  action?: ReactNode;
  /**
   * `filtered` is not a weaker `first-use`, and conflating them is the mistake §1.2 names.
   *
   * A first-use empty offers the action that creates the first thing. A filtered-to-nothing empty
   * must say *which filter to loosen* — telling somebody with three filters applied that they have
   * no candidates is simply false, and it sends them to the wrong fix.
   */
  variant?: 'first-use' | 'filtered';
}

export function EmptyState({ title, description, action, variant = 'first-use' }: EmptyStateProps) {
  return (
    <div className={`tf-empty-state tf-empty-state--${variant}`}>
      <p className="tf-empty-state__title">{title}</p>
      {description ? <p className="tf-empty-state__description">{description}</p> : null}
      {action ? <div className="tf-empty-state__action">{action}</div> : null}
    </div>
  );
}

export interface SkeletonProps {
  /**
   * Shaped like what is coming — §1.2: "skeleton loaders shaped like the real content (never
   * spinners)". A spinner says "wait"; a skeleton says "a table with six rows is coming", which is
   * the same information plus the layout, so nothing jumps when the data lands.
   */
  shape?: 'table' | 'cards' | 'lines';
  /** Rows, cards or lines to draw. Match it to what the screen usually shows. */
  count?: number;
}

export function Skeleton({ shape = 'lines', count = 4 }: SkeletonProps) {
  return (
    // One live region for the whole block rather than per row, and polite rather than assertive:
    // "loading" should not interrupt whatever a screen reader is in the middle of saying.
    <div className={`tf-skeleton tf-skeleton--${shape}`} aria-busy="true" aria-live="polite">
      {Array.from({ length: count }, (_, index) => (
        <span key={index} className="tf-skeleton__item" />
      ))}
    </div>
  );
}

/* ── Form controls ──────────────────────────────────────────────────────────────────────────────
 *
 * These exist because `.tf-input` was a CSS class with no component behind it, and a class you have
 * to remember is a class somebody forgets. Twenty-five files applied it; seven rendered a bare
 * `<select>` or `<input>` inside `Field` and got `font: inherit` from the base sheet and nothing
 * else — so two visual styles of control shipped side by side on the same screens.
 *
 * `Field` deliberately stays a render-prop and is *not* replaced by these: it owns the label, hint
 * and error, and a component per input type would multiply with every control the product needs.
 * What changes is that the control it renders is now a component too, so the styling is the default
 * rather than a convention.
 */

/*
 * `ComponentPropsWithRef` rather than `…HTMLAttributes`, so `ref` passes through.
 *
 * Two screens focus their filter input from a keyboard shortcut — the `/` binding — and they hold a
 * ref to do it. React 19 makes `ref` an ordinary prop on a function component, so this needs no
 * `forwardRef`; it only needs the prop type to admit what the runtime already accepts.
 *
 * `className` and `style` stay omitted. That is FE-05: a screen that could pass either would put a
 * physical property in the app, where the gate scoped to this package would never see it.
 */
export type InputProps = Omit<ComponentPropsWithRef<'input'>, 'className' | 'style'>;

export function Input(props: InputProps) {
  return <input {...props} className="tf-input" />;
}

export type TextareaProps = Omit<ComponentPropsWithRef<'textarea'>, 'className' | 'style'>;

export function Textarea(props: TextareaProps) {
  return <textarea {...props} className="tf-input tf-input--area" />;
}

export type SelectProps = Omit<ComponentPropsWithRef<'select'>, 'className' | 'style'>;

/**
 * A native `select`, restyled — not a custom listbox.
 *
 * The native control already has the keyboard model, the mobile picker, form association and type-
 * ahead. What it lacked here was looking like the rest of the product. Where a list is long enough
 * that a native select stops being usable — a candidate picker over four hundred people — the answer
 * is `Combobox` below, not a bigger select.
 */
export function Select({ children, ...rest }: SelectProps) {
  return (
    <select {...rest} className="tf-input tf-input--select">
      {children}
    </select>
  );
}

/* ── Combobox ──────────────────────────────────────────────────────────────────────────────────── */

export interface ComboboxOption {
  value: string;
  label: string;
  /** Optional second line — a title, a client, an email. What disambiguates two identical names. */
  detail?: string;
}

export interface ComboboxProps {
  /** Given by `Field` so the label points at the text input. */
  id?: string;
  options: readonly ComboboxOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  /** Shown when nothing is selected and the box is empty. */
  placeholder?: string;
  /** Announced when the filter matches nothing. Required: the package ships no English. */
  emptyLabel: string;
  /**
   * Announced after each keystroke as "<n> <resultsLabel>". Supply the *plural noun only* — the
   * count is prepended — so a caller's catalogue keeps the pluralisation rules its language needs.
   */
  resultsLabel: (count: number) => string;
  /** Names what the clear button clears, e.g. "Clear candidate". Never just "Clear". */
  clearLabel: string;
  disabled?: boolean;
  'aria-invalid'?: boolean;
}

let sequence = 0;

/**
 * A filtering single-select over a list too long for a native `select`.
 *
 * Every choice in the product was a native `<select>` — including candidate pickers over two to five
 * hundred people, which is the most acute day-to-day usability problem the UX inventory found. A
 * native select has no filter: you either know the name well enough to type-ahead it exactly, or you
 * scroll.
 *
 * **The listbox is not a popup library.** It is a `ul` positioned under the input, which means it can
 * be clipped by an ancestor's `overflow` — accepted deliberately, because the alternative is a portal
 * and a focus-management problem, and every current caller is inside a card that does not clip.
 *
 * **Selection commits on Enter or click, never on blur or on arrow.** Arrowing through options moves
 * `aria-activedescendant` and nothing else, so a screen reader reads each option without the value
 * changing underneath the user — the failure mode of a listbox that commits on move.
 */
export function Combobox({
  id,
  options,
  value,
  onChange,
  placeholder,
  emptyLabel,
  resultsLabel,
  clearLabel,
  disabled = false,
  'aria-invalid': invalid = false,
}: ComboboxProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useRef(`tf-combobox-${(sequence += 1)}`).current;
  /* Floats, for the same reason the row menu does — a combobox inside a panel was clipped by the
     panel's own corner radius. See `./floating.ts`. */
  const { triggerRef, floatingRef, style } = useFloating<HTMLDivElement, HTMLUListElement>(open);

  const selected = options.find((option) => option.value === value) ?? null;

  // When closed the box shows the selection; when open it shows what is being typed. Without this
  // split, opening a box with something already chosen would either wipe the selection or leave the
  // user editing a value they have to clear before they can search.
  const text = open ? query : (selected?.label ?? '');

  const needle = query.trim().toLowerCase();
  const matches =
    open && needle !== ''
      ? options.filter(
          (option) =>
            option.label.toLowerCase().includes(needle) ||
            (option.detail?.toLowerCase().includes(needle) ?? false),
        )
      : options;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const commit = (option: ComboboxOption | undefined) => {
    if (!option) return;
    onChange(option.value);
    setQuery('');
    setOpen(false);
    inputRef.current?.focus();
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        setActive(0);
        return;
      }
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => {
        if (matches.length === 0) return 0;
        return (current + step + matches.length) % matches.length;
      });
      return;
    }
    if (event.key === 'Home' && open) {
      event.preventDefault();
      setActive(0);
      return;
    }
    if (event.key === 'End' && open) {
      event.preventDefault();
      setActive(Math.max(0, matches.length - 1));
      return;
    }
    if (event.key === 'Enter' && open) {
      event.preventDefault();
      commit(matches[active]);
      return;
    }
    if (event.key === 'Escape' && open) {
      // Stops here rather than bubbling: Escape inside an open listbox closes the listbox, and a
      // drawer or dialog further up should not also close on the same keystroke.
      event.preventDefault();
      event.stopPropagation();
      setQuery('');
      setOpen(false);
    }
  };

  return (
    <div className="tf-combobox" ref={rootRef}>
      {/* The control, not the input, anchors the list: the list should line up with the field's
          own edge including its clear button, not with the text box inside it. */}
      <div className="tf-combobox__control" ref={triggerRef}>
        <input
          {...(id ? { id } : {})}
          ref={inputRef}
          type="text"
          className="tf-input tf-combobox__input"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-invalid={invalid || undefined}
          aria-activedescendant={open && matches[active] ? `${listId}-${active}` : undefined}
          autoComplete="off"
          disabled={disabled}
          value={text}
          placeholder={placeholder}
          onChange={(event) => {
            setQuery(event.currentTarget.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {selected && !disabled ? (
          <button
            type="button"
            className="tf-combobox__clear"
            aria-label={clearLabel}
            onClick={() => {
              onChange(null);
              setQuery('');
              inputRef.current?.focus();
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        ) : null}
      </div>

      {/* Polite, and outside the listbox: the result count is context, not the thing being read. */}
      <span className="tf-visually-hidden" aria-live="polite">
        {open ? resultsLabel(matches.length) : ''}
      </span>

      {open ? (
        <ul
          className="tf-combobox__list"
          id={listId}
          role="listbox"
          ref={floatingRef}
          popover="manual"
          style={style}
        >
          {matches.length === 0 ? (
            <li className="tf-combobox__empty" role="presentation">
              {emptyLabel}
            </li>
          ) : (
            matches.map((option, index) => (
              <li
                key={option.value}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={option.value === value}
                className={`tf-combobox__option${
                  index === active ? ' tf-combobox__option--active' : ''
                }`}
                // `pointerdown` rather than `click`: the input's blur would otherwise close the list
                // before the click landed on it.
                onPointerDown={(event) => {
                  event.preventDefault();
                  commit(option);
                }}
                onPointerEnter={() => setActive(index)}
              >
                <span className="tf-combobox__label">{option.label}</span>
                {option.detail ? (
                  <span className="tf-combobox__detail">{option.detail}</span>
                ) : null}
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

/* ── Page header ───────────────────────────────────────────────────────────────────────────────── */

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** A "back to X" link, rendered above the title. Deep screens had been hand-rolling this. */
  back?: ReactNode;
  /** Status chips, counts — what qualifies the title rather than acting on it. */
  meta?: ReactNode;
  /**
   * How the screen is being *displayed* — a view switcher, a density control, a column chooser.
   *
   * Separate from `actions`, which act on the subject: adding a job, scheduling a round, exporting.
   * Two reasons for the split rather than one bag of controls at the end of the row. They answer
   * different questions, so running them together makes a reader parse a row of six to find the one
   * button that does something; and a display control is a *preference*, so it belongs beside the
   * screen it governs rather than in the row somebody scans for the primary act.
   *
   * Rendered first in the same top-inline-end cluster, with a hairline between the two groups.
   */
  tools?: ReactNode;
  actions?: ReactNode;
}

/**
 * The screen's title block.
 *
 * Two idioms had grown up in parallel: most screens used `Card` as their header, while `interviews`
 * and `standup` used `SectionTitle` plus a muted paragraph. They are different sizes, different
 * weights and different spacing, so which screen you were on changed what a page title looked like.
 * This is the one shape, and `Card` goes back to meaning "a section within a screen".
 */
export function PageHeader({ title, description, back, meta, tools, actions }: PageHeaderProps) {
  return (
    <header className="tf-page-header">
      {back ? <div className="tf-page-header__back">{back}</div> : null}
      <div className="tf-page-header__row">
        <div className="tf-page-header__body">
          <h1 className="tf-page-header__title">{title}</h1>
          {description ? <p className="tf-page-header__description">{description}</p> : null}
        </div>
        {tools || actions ? (
          <div className="tf-page-header__actions">
            {tools ? <div className="tf-page-header__tools">{tools}</div> : null}
            {tools && actions ? (
              <span className="tf-page-header__rule" aria-hidden="true" />
            ) : null}
            {actions}
          </div>
        ) : null}
      </div>
      {meta ? <div className="tf-page-header__meta">{meta}</div> : null}
    </header>
  );
}

/* ── A person's own client markers — CO4 §14 ────────────────────────────────────────────────────
 *
 * A named set rather than a colour picker. Free-form hex would let somebody choose a red
 * indistinguishable from `--status-critical` and turn a memory aid into an alarm; six is enough to
 * tell a handful of accounts apart and few enough that none of them collides with a status tone.
 *
 * **Exported as a runtime list because the app had been building the class name from a raw string**
 * out of a preference, which meant an unknown value produced a dot with no rule and no colour —
 * indistinguishable, to the reader, from having set none. With the list here, the screen that offers
 * the choice and the screen that draws it are reading from the same six.
 */
export const CLIENT_MARKERS = ['slate', 'violet', 'teal', 'amber', 'rose', 'lime'] as const;
export type ClientMarker = (typeof CLIENT_MARKERS)[number];

export interface ClientMarkProps {
  /** Null or absent renders the name alone, which is the state almost every client is in. */
  marker?: ClientMarker | null;
  /** The name. Never omitted — see below. */
  children: ReactNode;
}

/**
 * A client's name with the reader's own marker beside it.
 *
 * The dot is `aria-hidden` and never replaces the name: a colour standing in for a word is
 * unreadable to anybody who did not set it and unusable by anybody who cannot tell two of them
 * apart. It is a memory aid for one person, and the product assigns it no meaning.
 */
export function ClientMark({ marker, children }: ClientMarkProps) {
  return (
    <span className="tf-client-mark">
      {marker ? <span className={`tf-client-dot tf-client-dot--${marker}`} aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

export interface ClientMarkerPickerProps {
  value: ClientMarker | null;
  onChange: (next: ClientMarker | null) => void;
  /** Names the whole group — "Your colour for Acme Corp". */
  label: string;
  /** Names one swatch. The colours have names and a swatch with none is a coloured square. */
  optionLabel: (marker: ClientMarker) => string;
  /** Names the option that removes the marker. */
  clearLabel: string;
  disabled?: boolean;
}

/**
 * Choosing one of the six — the control CO4 §14 described and nothing ever offered.
 *
 * The preference existed, the route validated it, the table rendered it, and there was **no UI
 * anywhere to set one**: a feature reachable only by writing to the API by hand. This is the missing
 * half, and it is deliberately six swatches rather than a colour input, for the reason the token
 * comment gives — a free-form hex lets somebody pick a red indistinguishable from the critical tone
 * and turns a memory aid into an alarm.
 *
 * Every swatch carries a name in the accessibility tree, and "no colour" is one of the options
 * rather than a separate clear button: removing a marker is choosing among seven states, not undoing
 * a choice among six.
 */
export function ClientMarkerPicker({
  value,
  onChange,
  label,
  optionLabel,
  clearLabel,
  disabled = false,
}: ClientMarkerPickerProps) {
  return (
    <div className="tf-marker-picker" role="group" aria-label={label}>
      <button
        type="button"
        className={`tf-marker-picker__swatch tf-marker-picker__swatch--none${
          value === null ? ' tf-marker-picker__swatch--active' : ''
        }`}
        aria-label={clearLabel}
        aria-pressed={value === null}
        disabled={disabled}
        onClick={() => onChange(null)}
      />
      {CLIENT_MARKERS.map((marker) => (
        <button
          key={marker}
          type="button"
          className={`tf-marker-picker__swatch tf-client-dot--${marker}${
            value === marker ? ' tf-marker-picker__swatch--active' : ''
          }`}
          aria-label={optionLabel(marker)}
          aria-pressed={value === marker}
          disabled={disabled}
          onClick={() => onChange(marker)}
        />
      ))}
    </div>
  );
}

/* ── Status dot ────────────────────────────────────────────────────────────────────────────────── */

export interface StatusDotProps {
  /**
   * Required and a string, for the same reason `StatusChip.label` is: colour never carries meaning
   * alone. A dot that could take an optional label would eventually ship without one.
   */
  label: string;
  tone?: StatusTone;
}

/**
 * A status marker for dense rows, where a chip is too much furniture.
 *
 * Carbon's decision rule is the one followed here: icon indicators for content-heavy layouts that
 * need maximum attention, **shape indicators for smaller spaces or when users scan large amounts of
 * data** — which is a two-hundred-row candidate list exactly. The chip stays for headers and detail
 * panels, where there is room and only one of them.
 *
 * The dot is `aria-hidden` and the label is real text: a screen reader reads the status, and a
 * monochrome or colour-blind reader sees it.
 */
export function StatusDot({ label, tone = 'neutral' }: StatusDotProps) {
  return (
    <span className="tf-dot-status">
      <span className={`tf-dot tf-dot--${tone}`} aria-hidden="true" />
      {label}
    </span>
  );
}

/* ── Table selection and row actions ───────────────────────────────────────────────────────────── */

export interface SelectAllCheckboxProps {
  /** How many rows on this page are selected. */
  selected: number;
  /** How many rows the header checkbox governs. */
  total: number;
  onChange: (checked: boolean) => void;
  /** Names what is being selected, e.g. "Select all candidates on this page". */
  label: string;
}

/**
 * The header checkbox, with the third state the DOM has and JSX does not set for you.
 *
 * `indeterminate` is a *property*, not an attribute, so React cannot render it — it has to be
 * assigned to the node. Without it a partially-selected page shows an unchecked box, and clicking it
 * appears to do nothing on the rows already selected.
 */
export function SelectAllCheckbox({ selected, total, onChange, label }: SelectAllCheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);
  const all = total > 0 && selected === total;
  const some = selected > 0 && selected < total;

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = some;
  }, [some]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className="tf-select-all"
      checked={all}
      disabled={total === 0}
      aria-label={label}
      onChange={(event) => onChange(event.currentTarget.checked)}
    />
  );
}

export interface RowAction {
  label: string;
  onSelect: () => void;
  tone?: 'default' | 'danger';
  disabled?: boolean;
}

export interface RowActionsProps {
  actions: readonly RowAction[];
  /** Names the row, e.g. "Actions for Priya Ramanathan". Never bare "Actions" — see below. */
  label: string;
}

/**
 * The per-row overflow menu, and it is **persistent rather than revealed on hover**.
 *
 * Carbon's default is the same, and the reasoning that settles it is that hover-only actions have no
 * hover state at all on a touch device. (The accessibility failure is *not* WCAG 1.4.13, which
 * explicitly excludes things revealed on keyboard focus — it is 2.1.1 and touch. Worth stating
 * because the internet gets this one wrong.)
 *
 * `label` names the row because a screen reader running down twenty rows of "Actions, Actions,
 * Actions" has been told nothing about which row it is on.
 */
export function RowActions({ actions, label }: RowActionsProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useRef(`tf-row-actions-${(sequence += 1)}`).current;
  /*
   * The menu floats rather than sitting inside the row.
   *
   * Every table in this product lives in `.tf-table-scroll`, whose `overflow-x: auto` computes
   * `overflow-y` to `auto` as well — so a menu opened on one of the last rows used to lengthen the
   * container and produce a scrollbar instead of floating over the rows below. See `./floating.ts`.
   */
  const { triggerRef, floatingRef, style } = useFloating<HTMLButtonElement, HTMLDivElement>(open);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    // Focus goes back to the trigger, not to the document: a menu that closes into nowhere puts a
    // keyboard user back at the top of the page.
    triggerRef.current?.focus();
  };

  return (
    <div className="tf-row-actions" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="tf-row-actions__trigger"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        {/* A vertical ellipsis — the kebab. Reserved for contextual actions and nothing else. */}
        <span aria-hidden="true">⋮</span>
      </button>

      {open ? (
        <div
          className="tf-row-actions__menu"
          id={menuId}
          role="menu"
          ref={floatingRef}
          // `manual`: this menu already implements its own outside-click and Escape handling, and
          // `auto`'s light-dismiss would close the element while React still believed it open.
          popover="manual"
          style={style}
        >
          {actions.map((action, index) => (
            <button
              key={index}
              type="button"
              role="menuitem"
              className={`tf-row-actions__item${
                action.tone === 'danger' ? ' tf-row-actions__item--danger' : ''
              }`}
              disabled={action.disabled}
              onClick={() => {
                setOpen(false);
                // Focus goes home *before* the action runs, and the order is the whole point: an
                // action that opens a panel captures `document.activeElement` to return focus to
                // later, and if this menu item is still focused when that happens, the panel
                // captures an element that is about to unmount. Focus then has nowhere to go back
                // to and lands on the document — a keyboard user restarts at the top of the page.
                triggerRef.current?.focus();
                action.onSelect();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  event.stopPropagation();
                  close();
                }
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
